import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { tasksApi, contactsApi, organizationsApi } from "@/lib/api";
import { formatEstTs, formatTaskAction } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, X, Link as LinkIcon, Building2, User, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Task Workspace — consolidated build §2.
 *
 * Shows everything about ONE task in one place:
 *   - Task info (name, instructions, assignee, project, status, priority, due)
 *   - Working record (notes feed, linked contacts, linked orgs, references,
 *     follow-up, questions, completion outcome)
 *   - Activity history (audit log scoped to this task_id)
 *
 * Permissions mirror backend:
 *   view_tasks         → read the workspace
 *   create_notes       → add notes / links / references / update working fields
 *   edit_project_settings → delete task / delete others' notes
 *   admin              → everything
 */
export default function TaskWorkspacePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [linkContactOpen, setLinkContactOpen] = useState(false);
  const [linkOrgOpen, setLinkOrgOpen] = useState(false);
  const [addRefOpen, setAddRefOpen] = useState(false);
  const [refForm, setRefForm] = useState({ url: "", label: "" });

  const load = useCallback(async () => {
    try {
      const t = await tasksApi.get(id);
      setData(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load task");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="h-full flex items-center justify-center text-white/60" data-testid="task-workspace-loading">Loading task…</div>;
  }

  // Permissions come straight from the hydrated task response so the
  // Workspace UI matches the backend gate exactly (admin wildcard, member
  // access-level preset, viewer read-only). No second /projects fetch.
  const perms = data.permissions || {};
  const canWrite = !!(perms.is_admin || perms.create_notes || perms.create_tasks);
  const canDelete = !!(perms.is_admin || perms.edit_project_settings);

  const setField = async (patch) => {
    try {
      await tasksApi.update(id, patch);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const saveNote = async () => {
    if (!noteDraft.trim()) return;
    setSavingNote(true);
    try {
      await tasksApi.addNote(id, noteDraft.trim());
      setNoteDraft("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note");
    } finally { setSavingNote(false); }
  };
  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try { await tasksApi.deleteNote(id, noteId); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const unlinkContact = async (cid) => {
    try { await tasksApi.unlinkContact(id, cid); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unlink failed"); }
  };
  const unlinkOrg = async (oid) => {
    try { await tasksApi.unlinkOrganization(id, oid); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unlink failed"); }
  };
  const deleteRef = async (rid) => {
    try { await tasksApi.deleteReference(id, rid); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const removeTask = async () => {
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    try {
      await tasksApi.remove(id);
      toast.success("Task deleted");
      nav(-1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#0B0E14] text-white px-6 py-8" data-testid="task-workspace-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-white/50 hover:text-white text-sm" data-testid="task-workspace-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* INFO HEADER */}
        <div className="p-6 rounded-xl border border-white/10 bg-[#151A23]" data-testid="task-info-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Task</div>
              {canWrite ? (
                <Input value={data.name || ""} onChange={e => setData({ ...data, name: e.target.value })}
                       onBlur={() => setField({ name: data.name })}
                       className="text-xl font-semibold bg-transparent border-0 border-b border-white/10 rounded-none focus-visible:ring-0 px-0"
                       data-testid="task-name-input" />
              ) : (
                <div className="text-xl font-semibold" data-testid="task-name-display">{data.name}</div>
              )}
              <div className="mt-2 text-xs text-white/50">
                {data.entity?.name && <span>{data.entity.name}</span>}
                {data.project?.name && (
                  <> · <Link to={`/entity/${data.entity?.id || data.project.entity_id || ""}?tab=projects`}
                              className="hover:underline" data-testid="task-project-link">
                    {data.project.name}
                  </Link></>
                )}
              </div>
            </div>
            {canDelete && (
              <Button variant="ghost" onClick={removeTask}
                      className="text-red-400 hover:bg-red-500/10"
                      data-testid="task-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete task
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
            <TaskSelectField label="Status" value={data.status}
                              options={["Not started", "Open", "In Progress", "Blocked", "Done"]}
                              onChange={v => setField({ status: v })} disabled={!canWrite}
                              testid="task-status-select" />
            <TaskSelectField label="Priority" value={data.priority}
                              options={["Low", "Normal", "Medium", "High", "Critical"]}
                              onChange={v => setField({ priority: v })} disabled={!canWrite}
                              testid="task-priority-select" />
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-white/40">Due date</Label>
              <Input type="date" value={data.due_date || ""}
                     onChange={e => setData({ ...data, due_date: e.target.value })}
                     onBlur={() => setField({ due_date: data.due_date })}
                     disabled={!canWrite}
                     className="bg-white/5 border-white/10 text-sm mt-1"
                     data-testid="task-due-date" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-white/40">Assignee</Label>
              <div className="text-sm text-white/80 mt-1.5" data-testid="task-assignee-display">
                {(data.linked_contacts || []).find(c => c.id === data.assignee_contact_id)?.full_name || "Unassigned"}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <Label className="text-[11px] uppercase tracking-wider text-white/40">Instructions</Label>
            {canWrite ? (
              <Textarea rows={3} value={data.instructions || ""}
                        onChange={e => setData({ ...data, instructions: e.target.value })}
                        onBlur={() => setField({ instructions: data.instructions || "" })}
                        className="bg-white/5 border-white/10 text-sm mt-1"
                        placeholder="What the assignee needs to do, context, outcome expected…"
                        data-testid="task-instructions" />
            ) : (
              <div className="text-sm text-white/70 whitespace-pre-wrap mt-1" data-testid="task-instructions-display">
                {data.instructions || "No instructions."}
              </div>
            )}
          </div>
        </div>

        {/* WORKING RECORD */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {/* NOTES */}
            <section className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="task-notes-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg">Working Notes</h3>
                <span className="text-xs text-white/50">{(data.working_notes || []).length} entries</span>
              </div>
              {canWrite && (
                <div className="flex gap-2 mb-3">
                  <Textarea rows={2} value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                            placeholder="Add a working note (what you observed, decided, or learned)…"
                            className="bg-white/5 border-white/10 text-sm flex-1"
                            data-testid="task-note-input" />
                  <Button onClick={saveNote} disabled={savingNote || !noteDraft.trim()}
                          className="bg-white text-black hover:bg-white/90 self-end"
                          data-testid="task-note-add">Add note</Button>
                </div>
              )}
              <div className="space-y-2">
                {(data.working_notes || []).length === 0 && (
                  <div className="text-sm text-white/40 italic">No notes yet.</div>
                )}
                {(data.working_notes || []).slice().reverse().map(n => (
                  <div key={n.id} className="p-3 rounded border border-white/10 bg-white/[0.02]" data-testid={`task-note-${n.id}`}>
                    <div className="flex items-center justify-between text-[11px] text-white/50">
                      <span>{n.author_name} · {formatEstTs(n.created_at)}</span>
                      {(perms.is_admin || perms.edit_project_settings) && (
                        <button onClick={() => deleteNote(n.id)}
                                className="text-red-400 hover:underline"
                                data-testid={`task-note-delete-${n.id}`}>
                          Delete
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-white/85 whitespace-pre-wrap mt-1">{n.body}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* FOLLOW-UP + QUESTIONS + COMPLETION */}
            <WorkingField label="Follow-up"
              value={data.follow_up || ""}
              onBlur={(v) => setField({ follow_up: v })}
              disabled={!canWrite} testid="task-followup" />
            <WorkingField label="Questions / issues to flag"
              value={data.questions || ""}
              onBlur={(v) => setField({ questions: v })}
              disabled={!canWrite} testid="task-questions" />
            <WorkingField label="Completion evidence / outcome"
              value={data.completion_outcome || ""}
              onBlur={(v) => setField({ completion_outcome: v })}
              disabled={!canWrite} testid="task-outcome" />
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-4">
            {/* LINKED CONTACTS */}
            <LinkedList
              title="Linked Contacts" icon={<User className="w-3.5 h-3.5" />}
              items={(data.linked_contacts || []).filter(c => c.id !== data.assignee_contact_id)}
              render={(c) => <>{c.full_name}<span className="text-white/40"> · {c.role || c.company || c.email || ""}</span></>}
              canWrite={canWrite} onAdd={() => setLinkContactOpen(true)} onRemove={unlinkContact}
              testid="task-contacts" />

            {/* LINKED ORGANIZATIONS */}
            <LinkedList
              title="Linked Organizations" icon={<Building2 className="w-3.5 h-3.5" />}
              items={data.linked_organizations || []}
              render={(o) => <>{o.name}<span className="text-white/40"> · {o.website || ""}</span></>}
              canWrite={canWrite} onAdd={() => setLinkOrgOpen(true)} onRemove={unlinkOrg}
              testid="task-organizations" />

            {/* REFERENCES */}
            <section className="p-4 rounded-xl border border-white/10 bg-[#151A23]" data-testid="task-references-panel">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-sm flex items-center gap-1.5"><LinkIcon className="w-3.5 h-3.5" /> References</h3>
                {canWrite && <button onClick={() => setAddRefOpen(true)} className="text-white/60 hover:text-white" data-testid="task-reference-add"><Plus className="w-4 h-4" /></button>}
              </div>
              <div className="space-y-1.5">
                {(data.references || []).length === 0 && <div className="text-xs text-white/40 italic">No references.</div>}
                {(data.references || []).map(r => (
                  <div key={r.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`task-reference-${r.id}`}>
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline truncate">{r.label || r.url}</a>
                    {canWrite && <button onClick={() => deleteRef(r.id)} className="text-red-400 hover:underline" data-testid={`task-reference-remove-${r.id}`}><X className="w-3 h-3" /></button>}
                  </div>
                ))}
              </div>
              {addRefOpen && (
                <div className="mt-2 space-y-1.5">
                  <Input placeholder="URL" value={refForm.url} onChange={e => setRefForm({ ...refForm, url: e.target.value })} className="bg-white/5 border-white/10 text-xs" data-testid="task-reference-url" />
                  <Input placeholder="Label (optional)" value={refForm.label} onChange={e => setRefForm({ ...refForm, label: e.target.value })} className="bg-white/5 border-white/10 text-xs" data-testid="task-reference-label" />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={async () => {
                      if (!refForm.url.trim()) return;
                      try {
                        await tasksApi.addReference(id, refForm.url.trim(), refForm.label.trim());
                        setRefForm({ url: "", label: "" });
                        setAddRefOpen(false);
                        await load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
                    }} className="bg-white text-black hover:bg-white/90 text-xs h-7" data-testid="task-reference-save">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddRefOpen(false)} className="text-xs h-7">Cancel</Button>
                  </div>
                </div>
              )}
            </section>

            {/* ACTIVITY */}
            <section className="p-4 rounded-xl border border-white/10 bg-[#151A23]" data-testid="task-activity-panel">
              <h3 className="font-display text-sm flex items-center gap-1.5 mb-2"><Clock className="w-3.5 h-3.5" /> Activity</h3>
              <div className="space-y-1.5">
                {(data.activity || []).length === 0 && <div className="text-xs text-white/40 italic">No activity yet.</div>}
                {(data.activity || []).map(a => (
                  <div key={a.id} className="text-[11px] text-white/60" data-testid={`task-activity-${a.id}`}>
                    <span className="text-white/85">{a.actor_name}</span> {formatTaskAction(a.action)} <span className="text-white/40">· {formatEstTs(a.created_at)}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      <ContactPicker open={linkContactOpen} onClose={() => setLinkContactOpen(false)}
        onPick={async (cid) => {
          try { await tasksApi.linkContact(id, cid); setLinkContactOpen(false); await load(); }
          catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
        }} exclude={new Set([data.assignee_contact_id, ...(data.linked_contact_ids || [])].filter(Boolean))} />

      <OrgPicker open={linkOrgOpen} onClose={() => setLinkOrgOpen(false)}
        onPick={async (oid) => {
          try { await tasksApi.linkOrganization(id, oid); setLinkOrgOpen(false); await load(); }
          catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
        }} exclude={new Set(data.linked_organization_ids || [])} />
    </div>
  );
}

function TaskSelectField({ label, value, options, onChange, disabled, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-white/40">{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="bg-white/5 border-white/10 text-sm mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function WorkingField({ label, value, onBlur, disabled, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <section className="p-5 rounded-xl border border-white/10 bg-[#151A23]">
      <h3 className="font-display text-sm mb-2">{label}</h3>
      {disabled ? (
        <div className="text-sm text-white/70 whitespace-pre-wrap" data-testid={`${testid}-display`}>{v || "—"}</div>
      ) : (
        <Textarea rows={2} value={v || ""} onChange={e => setV(e.target.value)} onBlur={() => onBlur(v)}
                  className="bg-white/5 border-white/10 text-sm" data-testid={testid} />
      )}
    </section>
  );
}

function LinkedList({ title, icon, items, render, canWrite, onAdd, onRemove, testid }) {
  return (
    <section className="p-4 rounded-xl border border-white/10 bg-[#151A23]" data-testid={`${testid}-panel`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-sm flex items-center gap-1.5">{icon} {title}</h3>
        {canWrite && <button onClick={onAdd} className="text-white/60 hover:text-white" data-testid={`${testid}-add`}><Plus className="w-4 h-4" /></button>}
      </div>
      <div className="space-y-1.5">
        {items.length === 0 && <div className="text-xs text-white/40 italic">None linked.</div>}
        {items.map(it => (
          <div key={it.id} className="flex items-center justify-between gap-2 text-xs text-white/85" data-testid={`${testid}-row-${it.id}`}>
            <div className="truncate">{render(it)}</div>
            {canWrite && <button onClick={() => onRemove(it.id)} className="text-red-400 hover:underline" data-testid={`${testid}-remove-${it.id}`}><X className="w-3 h-3" /></button>}
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactPicker({ open, onClose, onPick, exclude }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { if (!open) return;
    (async () => { try { const r = await contactsApi.list({ q: q || undefined }); setRows((r.data || []).filter(c => !exclude?.has(c.id))); } catch {}
    })();
  }, [open, q, exclude]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose} data-testid="task-link-contact-dialog">
      <div className="bg-[#151A23] border border-white/10 rounded-xl max-w-md w-full p-4" onClick={e => e.stopPropagation()}>
        <div className="font-display text-lg mb-3">Link a Contact</div>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="bg-white/5 border-white/10 text-sm" data-testid="task-link-contact-search" />
        <div className="max-h-72 overflow-auto mt-2 space-y-1">
          {rows.slice(0, 50).map(c => (
            <button key={c.id} onClick={() => onPick(c.id)} className="w-full text-left p-2 rounded hover:bg-white/5 text-sm" data-testid={`task-link-contact-pick-${c.id}`}>
              {c.full_name}<span className="text-white/40 text-xs"> · {c.role || c.company || c.email || ""}</span>
            </button>
          ))}
          {rows.length === 0 && <div className="text-sm text-white/40 p-2">No matching contacts.</div>}
        </div>
      </div>
    </div>
  );
}

function OrgPicker({ open, onClose, onPick, exclude }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { if (!open) return;
    (async () => { try { const r = await organizationsApi.list({ q: q || undefined }); setRows((r.data?.rows || r.data || []).filter(o => !exclude?.has(o.id))); } catch {}
    })();
  }, [open, q, exclude]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose} data-testid="task-link-org-dialog">
      <div className="bg-[#151A23] border border-white/10 rounded-xl max-w-md w-full p-4" onClick={e => e.stopPropagation()}>
        <div className="font-display text-lg mb-3">Link an Organization</div>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="bg-white/5 border-white/10 text-sm" data-testid="task-link-org-search" />
        <div className="max-h-72 overflow-auto mt-2 space-y-1">
          {rows.slice(0, 50).map(o => (
            <button key={o.id} onClick={() => onPick(o.id)} className="w-full text-left p-2 rounded hover:bg-white/5 text-sm" data-testid={`task-link-org-pick-${o.id}`}>
              {o.name}<span className="text-white/40 text-xs"> · {o.website || ""}</span>
            </button>
          ))}
          {rows.length === 0 && <div className="text-sm text-white/40 p-2">No matching organizations.</div>}
        </div>
      </div>
    </div>
  );
}
